import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

interface UsePCComparisonResult {
  selectedPCIdsToCompare: string[];
  handleAddToCompare: (id: string | null | string[]) => void;
  handleCloseCompare: () => void;
  doOpenCompare: (selectedPCIds: string[]) => boolean;
  getPCsToCompare: (pcs: Compound[]) => (Compound | null)[];
}

const usePCComparison = (): UsePCComparisonResult => {
  const [selectedPCIdsToCompare, setSelectedPCIdsToCompare] = useState<
    string[]
  >(() => {
    const storedIds = localStorage.getItem("comparePCs");
    return storedIds ? JSON.parse(storedIds) : [];
  });

  const handleAddToCompare = useCallback((id: string | null | string[]) => {
    if (!id) {
      setSelectedPCIdsToCompare([]);
      return;
    }
    if (Array.isArray(id)) {
      setSelectedPCIdsToCompare(id);
      return;
    }
    setSelectedPCIdsToCompare((prevIds) => {
      if (prevIds.includes(id)) {
        return prevIds.filter((pcId) => pcId !== id);
      }
      return [...prevIds, id];
    });
  }, []);

  const handleCloseCompare = useCallback(() => {
    setSelectedPCIdsToCompare([]);
  }, []);

  const doOpenCompare = useCallback((selectedPCIds: string[]) => {
    return selectedPCIds.length >= 2;
  }, []);

  const getPCsToCompare = useCallback(
    (allPCs: Compound[]) => {
      return selectedPCIdsToCompare.map((id) => {
        return allPCs.find((pc) => pc.id === id) || null;
      });
    },
    [selectedPCIdsToCompare],
  );

  useEffect(() => {
    localStorage.setItem("comparePCs", JSON.stringify(selectedPCIdsToCompare));
  }, [selectedPCIdsToCompare]);

  return {
    selectedPCIdsToCompare,
    handleAddToCompare,
    handleCloseCompare,
    doOpenCompare,
    getPCsToCompare,
  };
};

export default usePCComparison;
